import { useCallback, useEffect, useMemo, useState, type RefObject } from "react";
import type { TableSortState } from "../ctx";
import type { ensureTableElementSettings } from "../utils/ensure-table-element-settings";
import {
  applyFilters,
  type ColumnFilter,
  hasActiveFilters,
  type TableFilterState,
} from "../utils/apply-filters";
import { deriveColumnKind } from "../utils/derive-column-kind";
import { deriveColumnUniqueValues } from "../utils/derive-column-unique-values";
import { sortRowOrder } from "../utils/sort-row-order";

type TableElementSettings = ReturnType<typeof ensureTableElementSettings>;

interface IUseTableSortFilterArgs {
  elementSettings: TableElementSettings;
  isEditor: boolean;
  containerRef: RefObject<HTMLDivElement | null>;
}

export const useTableSortFilter = ({
  elementSettings,
  isEditor,
  containerRef,
}: IUseTableSortFilterArgs) => {
  const readerMode = !isEditor;
  const sortGated = readerMode && elementSettings.tableAllowSorting === true;
  const filterGated =
    readerMode && elementSettings.tableAllowFiltering === true;

  const [sortState, setSortState] = useState<TableSortState>(null);
  const [filterState, setFilterState] = useState<TableFilterState>(
    () => new Map(),
  );
  const [openFilterCol, setOpenFilterCol] = useState<number | null>(null);
  const [headerMenuCol, setHeaderMenuCol] = useState<number | null>(null);

  const effectiveSortState = sortGated ? sortState : null;
  const effectiveFilterState = filterGated ? filterState : undefined;

  const handleHeaderTriggerClick = useCallback((col: number) => {
    setOpenFilterCol(null);
    setHeaderMenuCol((prev) => (prev === col ? null : col));
  }, []);

  const handleSortApply = useCallback(
    (col: number, direction: "asc" | "desc" | null) => {
      setSortState(direction ? { col, direction } : null);
      setHeaderMenuCol(null);
    },
    [],
  );

  const handleFilterTriggerClick = useCallback((col: number) => {
    setHeaderMenuCol(null);
    setOpenFilterCol((prev) => (prev === col ? null : col));
  }, []);

  const handleFilterChange = useCallback(
    (col: number, next: ColumnFilter | null) => {
      setFilterState((prev) => {
        const m = new Map(prev);
        if (next === null) m.delete(col);
        else m.set(col, next);
        return m;
      });
    },
    [],
  );

  const rowOrder = useMemo(() => {
    const sorted =
      sortGated && sortState
        ? sortRowOrder(elementSettings, sortState.col, sortState.direction)
        : null;
    const filterActive = filterGated && hasActiveFilters(filterState);
    if (!sorted && !filterActive) return undefined;
    const base =
      sorted ?? Array.from({ length: elementSettings.tableRows }, (_, i) => i);
    if (!filterActive) return base;
    return applyFilters(base, elementSettings, filterState);
  }, [sortGated, sortState, filterGated, filterState, elementSettings]);

  const columnKindByCol = useMemo<("number" | "text")[] | undefined>(() => {
    if (!filterGated) return undefined;
    return Array.from({ length: elementSettings.tableCols }, (_, c) =>
      deriveColumnKind(elementSettings, c),
    );
  }, [filterGated, elementSettings]);

  const uniqueValuesByCol = useMemo(() => {
    if (!filterGated) return undefined;
    return Array.from({ length: elementSettings.tableCols }, (_, c) =>
      deriveColumnUniqueValues(elementSettings, c),
    );
  }, [filterGated, elementSettings]);

  useEffect(() => {
    if (headerMenuCol === null && openFilterCol === null) return;
    const handleMouse = (e: MouseEvent) => {
      const target = e.target as Node | null;
      if (containerRef.current?.contains(target)) return;
      const el = target as HTMLElement | null;
      if (el?.closest?.(".table-cell-portal-menu")) return;
      setHeaderMenuCol(null);
      setOpenFilterCol(null);
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setHeaderMenuCol(null);
        setOpenFilterCol(null);
      }
    };
    document.addEventListener("mousedown", handleMouse);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleMouse);
      document.removeEventListener("keydown", handleKey);
    };
  }, [headerMenuCol, openFilterCol, containerRef]);

  return {
    sortGated,
    filterGated,
    effectiveSortState,
    effectiveFilterState,
    headerMenuCol,
    openFilterCol,
    rowOrder,
    columnKindByCol,
    uniqueValuesByCol,
    handleHeaderTriggerClick,
    handleSortApply,
    handleFilterTriggerClick,
    handleFilterChange,
  };
};
